/**
 * Header settings menu.
 *
 * The header is deliberately one line of brand plus one control, so every
 * low-frequency entry point moved in here: language, the account actions, the
 * admin console, the full explanation pages and the feedback address.
 *
 * The panel is rendered in place and anchored under the header on wide screens,
 * while on a phone it becomes a bottom sheet. Either way it behaves like a
 * dialog: focus moves in when it opens, Escape and outside clicks close it, focus
 * returns to the trigger, the background does not scroll, and the tab order stays
 * inside the panel until it closes.
 */

import { ExternalLink, Globe, LogOut, ShieldCheck, UserRound, X } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { CONTACT_EMAIL } from '../shared/contact-card'
import { SUPPORTED_LOCALES, type Locale } from '@shared/locales'

import { cn } from '../../lib/format'
import { LOCALE_LABELS } from '../../lib/i18n'

export interface LandingSettingsMenuProps {
  open: boolean
  onClose: () => void
  /** Locale currently rendered by the app. */
  locale: Locale
  /** Switches the UI language; the caller persists it. */
  onSelectLocale: (locale: Locale) => void
  /** Signed-in nickname, or `null` for an anonymous visitor. */
  nickname: string | null
  isAuthenticated: boolean
  /** Shows the console entry for any account holding a capability. */
  canOpenAdmin: boolean
  onLogout: () => void
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground px-2 pt-2 text-xs font-medium tracking-wide uppercase">
      {children}
    </p>
  )
}

/** Shared shape of every row in the panel, sized for a touch target. */
const rowClass = cn(
  'flex min-h-11 w-full items-center gap-1 rounded-md px-2 py-2 text-left text-sm transition-colors',
  'hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground outline-none',
)

export function LandingSettingsMenu({
  open,
  onClose,
  locale,
  onSelectLocale,
  nickname,
  isAuthenticated,
  canOpenAdmin,
  onLogout,
}: LandingSettingsMenuProps) {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement>(null)
  // The dialog key handler must see the current closer without re-running the
  // whole effect (and re-focusing the first row) on every parent render.
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!open) {
      return
    }

    // Focus the first control so the keyboard lands inside the panel.
    const first = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    first?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeRef.current()
        return
      }
      if (event.key !== 'Tab') {
        return
      }

      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (!focusable || focusable.length === 0) {
        return
      }

      const firstItem = focusable[0] as HTMLElement
      const lastItem = focusable[focusable.length - 1] as HTMLElement
      const active = document.activeElement

      if (event.shiftKey && (active === firstItem || !panelRef.current?.contains(active))) {
        event.preventDefault()
        lastItem.focus()
      } else if (!event.shiftKey && active === lastItem) {
        event.preventDefault()
        firstItem.focus()
      }
    }

    function onPointerDown(event: PointerEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        closeRef.current()
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    // `pointerdown` so a click that starts inside and ends outside does not close.
    document.addEventListener('pointerdown', onPointerDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  if (!open) {
    return null
  }

  const locales = SUPPORTED_LOCALES as readonly Locale[]

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-start sm:justify-end sm:p-4">
      {/* Backdrop: closes the panel, never the only way out. */}
      <div className="absolute inset-0 bg-black/40" aria-hidden onClick={onClose} />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('home.settingsTitle')}
        className="bg-popover text-popover-foreground relative flex max-h-[85dvh] w-full flex-col gap-1 overflow-y-auto rounded-t-2xl border p-3 shadow-lg sm:mt-14 sm:max-w-xs sm:rounded-2xl"
      >
        <div className="flex items-center justify-between gap-2 px-2 pb-1">
          <p className="text-sm font-semibold">{t('home.settingsTitle')}</p>
          <button
            type="button"
            aria-label={t('common.close')}
            className="hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring inline-flex size-9 items-center justify-center rounded-md outline-none focus-visible:ring-2"
            onClick={onClose}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <SectionLabel>{t('home.settingsLanguage')}</SectionLabel>
        <div className="flex gap-2 px-1 pb-1">
          {locales.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={value === locale}
              className={cn(
                'min-h-11 flex-1 rounded-md border px-3 text-sm transition-colors',
                value === locale
                  ? 'border-primary bg-primary/10 font-medium'
                  : 'border-border hover:bg-accent hover:text-accent-foreground',
              )}
              onClick={() => onSelectLocale(value)}
            >
              {LOCALE_LABELS[value]}
            </button>
          ))}
        </div>

        <SectionLabel>{isAuthenticated ? t('home.settingsAccount') : t('nav.login')}</SectionLabel>
        {isAuthenticated ? (
          <>
            <div className="text-muted-foreground flex items-center gap-2 px-2 pb-1 text-sm">
              <UserRound className="size-4" aria-hidden />
              <span className="truncate">{nickname ?? t('nav.me')}</span>
            </div>
            <Link className={rowClass} to="/me" onClick={onClose}>
              <UserRound className="size-4" aria-hidden />
              {t('nav.me')}
            </Link>
            {canOpenAdmin ? (
              <Link className={rowClass} to="/admin" onClick={onClose}>
                <ShieldCheck className="size-4" aria-hidden />
                {t('nav.admin')}
              </Link>
            ) : null}
            <button type="button" className={rowClass} onClick={onLogout}>
              <LogOut className="size-4" aria-hidden />
              {t('nav.logout')}
            </button>
          </>
        ) : (
          <Link className={rowClass} to="/login/email" onClick={onClose}>
            <UserRound className="size-4" aria-hidden />
            {t('nav.login')}
          </Link>
        )}

        <SectionLabel>{t('common.appName')}</SectionLabel>
        <Link className={rowClass} to="/guide" onClick={onClose}>
          <Globe className="size-4" aria-hidden />
          {t('home.settingsGuide')}
        </Link>
        <Link className={rowClass} to="/about" onClick={onClose}>
          <Globe className="size-4" aria-hidden />
          {t('home.settingsAbout')}
        </Link>
        <a className={rowClass} href={`mailto:${CONTACT_EMAIL}`}>
          <ExternalLink className="size-4" aria-hidden />
          {t('home.settingsContact')}
        </a>
      </div>
    </div>
  )
}
